<script setup>
import { computed } from 'vue';

/**
 * Pagineringsfoden under produktlisten.
 *
 * Komponenten hed engang det den var, en tabelvisning af produkter, men
 * listen tegnes nu af ProductListRow, ProductCompactRow og ProductGridCard
 * ovenfor. Tilbage er kun sidenavigationen. Sorteringen lå her indtil
 * 2026-08-25, altså under hele listen, hvor ingen kunne finde den. Den bor nu
 * i værktøjslinjen over listen.
 */
const props = defineProps({
    loadNextPage: { type: Function, required: true },
    loadPrevPage: { type: Function, required: true },
    loadSpecificPage: { type: Function, required: true },
    currentPage: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
});

// Sidetal med ellipser: altid første og sidste, plus naboerne til den aktuelle.
const pageNumbers = computed(() => {
    if (props.totalPages <= 7) {
        return Array.from({ length: props.totalPages }, (_, i) => i + 1);
    }

    const pages = [1];

    if (props.currentPage > 3) {
        pages.push('...');
    }

    const start = Math.max(2, props.currentPage - 1);
    const end = Math.min(props.totalPages - 1, props.currentPage + 1);
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }

    if (props.currentPage < props.totalPages - 2) {
        pages.push('...');
    }

    if (props.totalPages > 1) {
        pages.push(props.totalPages);
    }

    return pages;
});
</script>

<template>
    <nav v-if="totalPages > 1" aria-label="Sider med produkter" class="produkt-paginering">
        <div class="pagination-container d-flex justify-content-between align-items-center">
            <div class="page-info">Side {{ currentPage }} af {{ totalPages }}</div>
            <ul class="pagination">
                <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                    <a class="page-link" href="#" @click.prevent="loadPrevPage" aria-label="Forrige side">
                        <span aria-hidden="true">&laquo;</span>
                    </a>
                </li>

                <li
                    v-for="(page, i) in pageNumbers"
                    :key="`${page}-${i}`"
                    class="page-item"
                    :class="{ active: page === currentPage, disabled: page === '...' }"
                >
                    <span v-if="page === '...'" class="page-link">{{ page }}</span>
                    <a
                        v-else
                        class="page-link"
                        href="#"
                        :aria-current="page === currentPage ? 'page' : undefined"
                        @click.prevent="loadSpecificPage(page)"
                    >{{ page }}</a>
                </li>

                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                    <a class="page-link" href="#" @click.prevent="loadNextPage" aria-label="Næste side">
                        <span aria-hidden="true">&raquo;</span>
                    </a>
                </li>
            </ul>
        </div>
    </nav>
</template>

<style scoped>
/*
 * Farverne var hårdkodet til #fff, #f8f9fa og #1E3A8A. Mørkt tema er standard
 * på sitet, så sidetallene stod som en lys kasse nederst på en mørk side.
 * Alt herunder følger nu de samme tokens som resten af siden.
 */
.produkt-paginering {
    padding: 12px 16px;
}

.pagination-container {
    width: 100%;
}

.page-info {
    color: var(--pc-text-muted);
    font-size: var(--pc-text-sm);
}

.pagination {
    margin-bottom: 0;
    gap: 2px;
}

.page-link {
    color: var(--pc-text-primary);
    background-color: var(--pc-bg-tertiary);
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius-sm, 0.375rem);
    font-weight: 500;
    transition: background-color 0.15s, color 0.15s, border-color 0.15s;
    margin: 0 2px;
    min-width: 38px;
    text-align: center;
}

.page-link:hover,
.page-link:focus {
    color: var(--pc-text-primary);
    background-color: var(--pc-bg-hover);
    border-color: var(--pc-border-hover);
    text-decoration: none;
}

.page-item.active .page-link {
    background-color: var(--pc-accent);
    border-color: var(--pc-accent);
    color: var(--pc-text-primary);
    font-weight: 600;
}

.page-item.disabled .page-link {
    color: var(--pc-text-muted);
    pointer-events: none;
    background-color: var(--pc-bg-secondary);
    border-color: var(--pc-border);
    opacity: 0.6;
}

@media (max-width: 576px) {
    .pagination-container {
        flex-direction: column;
        align-items: flex-start !important;
        gap: 0.5rem;
    }

    .page-info {
        margin-bottom: 0.5rem;
    }
}
</style>
